/// <amd-module name="Wit/WorkItemForm/Links/WorkItem/resolveWorkItemArtifacts" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLocationService } from "VSS/Platform/Location";
import { WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IWorkItemLinksService } from "Wit/Common/WorkItemLinksService/WorkItemLinksService";
import { IWorkItemStateColorMetadataService } from "Wit/UI/Services/WorkItemStateColorMetadataService";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";
import { RegisteredLinkTypeNames, ToolIds } from "Wit/WorkItemLinksRegistration/Constants";
import { ILinkIdentifier, IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";

interface ILinkIdentifierWithDefinedId extends ILinkIdentifier {
    ID: number;
}

export async function resolveWorkItemArtifacts(
    linkIdentifiers: ILinkIdentifier[],
    pageContext: IVssPageContext,
    workItemId?: number
): Promise<IResolvedLinkDisplayData[]> {
    const links: ILinkIdentifierWithDefinedId[] = linkIdentifiers.filter((l): l is ILinkIdentifierWithDefinedId => !!l.ID);
    const workItemIds = links.filter(l => l.ID > 0).map(l => l.ID);
    if (workItemIds.length === 0) {
        return [];
    }

    const linksService = pageContext.getService<IWorkItemLinksService>("IWorkItemLinksService");
    const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
    const workItemModelService = pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
    const uniqueIds = [...new Set<number>(workItemIds)];

    return await resolveWorkItemArtifactsFetchingNecessaryFields(uniqueIds, workItemId);

    async function resolveWorkItemArtifactsFetchingNecessaryFields(ids: number[], originalWorkItemId?: number): Promise<IResolvedLinkDisplayData[]> {
        const workItemService = pageContext.getService<IWorkItemService>("work-item-service");
        const metadataColorService = pageContext.getService<IWorkItemStateColorMetadataService>("work-item-state-color-metadata-service");
        const workItem = originalWorkItemId ? await workItemModelService.getWorkItem(originalWorkItemId) : undefined;

        const fields = [
            CoreFieldRefNames.Id,
            CoreFieldRefNames.Title,
            CoreFieldRefNames.State,
            CoreFieldRefNames.WorkItemType,
            CoreFieldRefNames.AssignedTo,
            CoreFieldRefNames.TeamProject,
            CoreFieldRefNames.ChangedDate
        ];

        const workItems = await workItemService.loadWorkItemsWithCaching(ids, fields, workItem?.project.guid);

        const idToWorkItemMap = new Map<number, WorkItem>(workItems.map(wi => [wi.id, wi]));

        return Promise.all(links.filter(link => idToWorkItemMap.has(link.ID)).map(toWorkItemLinkDisplayData));

        async function toWorkItemLinkDisplayData(linkIdentifier: ILinkIdentifierWithDefinedId): Promise<IResolvedLinkDisplayData> {
            const workItem = idToWorkItemMap.get(linkIdentifier.ID)!;
            const linkTypeEnd = linkIdentifier.LinkType ? await linksService.getWitLinkTypeEnd(linkIdentifier.LinkType) : undefined;

            const assignedTo = workItem.fields[CoreFieldRefNames.AssignedTo];
            const assignedIdentity = assignedTo?.identityRef ?? assignedTo;
            const stateColor = await getWorkItemStateColor(metadataColorService, workItem);

            return {
                linkIdentifier,
                icons: [
                    {
                        project: workItem.fields[CoreFieldRefNames.TeamProject],
                        workItemTypeName: workItem.fields[CoreFieldRefNames.WorkItemType]
                    },
                    assignedIdentity
                ],
                id: workItem.id,
                displayId: "" + workItem.id,
                title: workItem.fields[CoreFieldRefNames.Title],
                state: workItem.fields[CoreFieldRefNames.State],

                tool: ToolIds.WorkItemTracking,
                linkTypeDisplayName: linkTypeEnd?.name ?? RegisteredLinkTypeNames.Related,
                registeredLinkType: RegisteredLinkTypeNames.Related,
                witLinkTypeEnd: linkTypeEnd?.immutableName,

                updateDate: workItem.fields[CoreFieldRefNames.ChangedDate],
                uri: locationService.routeUrl("ms.vss-work-web.new-work-item-form-route", {
                    project: workItem.fields[CoreFieldRefNames.TeamProject],
                    id: "" + workItem.id
                }),
                additionalData: {
                    stateColor
                }
            };
        }
    }
}

async function getWorkItemStateColor(service: IWorkItemStateColorMetadataService, workItem: WorkItem): Promise<string | undefined> {
    let stateAndColor = service.getWorkItemStateColor(
        workItem.fields[CoreFieldRefNames.TeamProject],
        workItem.fields[CoreFieldRefNames.WorkItemType],
        workItem.fields[CoreFieldRefNames.State]
    );

    if (stateAndColor instanceof Promise) {
        stateAndColor = await stateAndColor;
    }

    return stateAndColor.fill ? `#${stateAndColor.fill}` : undefined;
}
